import { AbsoluteFill, Freeze, Sequence } from 'remotion';

import {
  CapsuleCoverTransition,
  getCapsuleCoverRevealFrame,
} from '../items/transition/capsule-cover-transition';
import { InnerLayer } from '../items/inner-layer';
import type { TransitionItem } from '../items/transition/transition-item-type';
import type { TrackType } from '../state/types';
import { useAllItems } from '../utils/use-context';

export function CapsuleCoverTransitionLayer({
  transitions,
  tracks,
}: {
  transitions: Record<string, TransitionItem>;
  tracks: TrackType[];
}) {
  const { items } = useAllItems();
  const visibleTracks = tracks.filter((track) => !track.hidden);
  const visibleTrackIds = new Set(visibleTracks.map((track) => track.id));
  const capsuleTransitions = Object.values(transitions).filter(
    (transition) =>
      transition.transitionType === 'capsuleCover' &&
      transition.durationInFrames > 0 &&
      visibleTrackIds.has(transition.trackId),
  );

  if (capsuleTransitions.length === 0) return null;

  return (
    <>
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        {capsuleTransitions.map((transition) => {
          const targetItem = items[transition.targetItemId];
          const targetTrackIndex = visibleTracks.findIndex(
            (track) => track.id === transition.trackId,
          );
          if (!targetItem || targetTrackIndex < 0) return null;

          const revealOffset = getCapsuleCoverRevealFrame(
            transition.durationInFrames,
          );
          const revealDuration = transition.durationInFrames - revealOffset;
          if (revealDuration <= 0) return null;

          return (
            <Sequence
              key={`target-preview-${transition.id}`}
              from={Math.max(0, transition.from + revealOffset)}
              durationInFrames={revealDuration}
              premountFor={Math.min(30, transition.durationInFrames)}
              style={{
                pointerEvents: 'none',
                zIndex: visibleTracks.length - targetTrackIndex,
              }}
            >
              <Freeze frame={0}>
                <InnerLayer
                  item={targetItem}
                  trackMuted
                  cropBackground={false}
                  zIndex={visibleTracks.length - targetTrackIndex}
                />
              </Freeze>
            </Sequence>
          );
        })}
      </AbsoluteFill>
      <AbsoluteFill style={{ pointerEvents: 'none', zIndex: 1000 }}>
        {capsuleTransitions.map((transition) => (
          <Sequence
            key={transition.id}
            from={Math.max(0, transition.from)}
            durationInFrames={transition.durationInFrames}
            premountFor={Math.min(30, transition.durationInFrames)}
          >
            <CapsuleCoverTransition
              primaryColor={transition.primaryColor}
              secondaryColor={transition.secondaryColor}
              durationInFrames={transition.durationInFrames}
            />
          </Sequence>
        ))}
      </AbsoluteFill>
    </>
  );
}
